/**
 * passPreview — the pass page's picture of a minted pass: the template's
 * CURRENT design with THIS pass's data on it, composed the way the Apple mint
 * composes it (partner-portal-api realAppleMinter design path): the seed from
 * the pass's own service + flight (lib/designSeed — the shared composition),
 * `applyLiveData` over the design, the live state through
 * `applyEventPatchToDesign`, then the per-pass extras and policy number as
 * refresh-or-append back rows. The old preview was a Google-style mockup in
 * the partner's portal colour — nothing like the Apple pass it sat next to.
 *
 * A preview, not the signed pass: it follows the design as it is NOW (an
 * edit after the mint shows up here, not on the phone until a re-render).
 */
import {
  applyEventPatchToDesign,
  applyLiveData,
  mergePassDesign,
  type FlightContext,
  type FlightStatusCode,
  type PassDesign,
  type PassDesignField,
  type ServicePayload,
} from '@insurwiz/wallet-core';

import type { DemoPartner, DemoPass } from '../types';

import { composeSeed } from './designSeed';

const PLACEHOLDER = '—';
const GROUPS = ['header', 'primary', 'secondary', 'auxiliary', 'back'] as const;

/** An empty flight context — the flightless travel and general passes have none. */
const NO_FLIGHT: FlightContext = {
  carrier: '',
  number: '',
  departureAirport: '',
  arrivalAirport: '',
  scheduledDeparture: '',
  scheduledArrival: '',
  actualDeparture: null,
  actualArrival: null,
  status: 'ON_TIME',
  delayMinutes: 0,
  gate: null,
  terminal: null,
};

/** Portal live state → the pass status vocabulary (what the pass says). */
const STATUS_FOR: Partial<Record<NonNullable<DemoPass['liveStatus']>['state'], FlightStatusCode>> =
  {
    on_time: 'ON_TIME',
    delayed: 'DELAYED',
    cancelled: 'CANCELLED',
    boarding: 'BOARDING',
    departed: 'DEPARTED',
    arrived: 'LANDED',
    diverted: 'DIVERTED',
  };

function payloadString(pass: DemoPass, key: string): string | null {
  const v = (pass.servicePayload ?? {})[key];
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/** The flight the pass tracks, or null (flightless travel / general pass). */
function flightOf(pass: DemoPass): FlightContext | null {
  if (!pass.flightIata || !pass.originIata || !pass.destinationIata) return null;
  // Same split as the API's parseFlightIata: a 2-char carrier, then the number.
  const m = /^([A-Z][A-Z0-9])(\d{1,4}[A-Z]?)$/.exec(pass.flightIata);
  return {
    ...NO_FLIGHT,
    carrier: m?.[1] ?? pass.flightIata.slice(0, 2),
    number: m?.[2] ?? pass.flightIata.slice(2),
    departureAirport: pass.originIata,
    arrivalAirport: pass.destinationIata,
    scheduledDeparture: pass.scheduledDepartureUtc ?? '',
    scheduledArrival: pass.scheduledArrivalUtc ?? '',
    gate: pass.liveStatus?.gate ?? null,
    terminal: pass.liveStatus?.terminal ?? null,
  };
}

/** The lounge render vehicle the mint derives (realAppleMinter deriveServiceAndFlight). */
function serviceOf(pass: DemoPass, flight: FlightContext | null): ServicePayload {
  if (pass.templateKind === 'travel') {
    return flight === null
      ? {
          kind: 'lounge',
          loungeName: PLACEHOLDER,
          loungeCode: PLACEHOLDER,
          accessWindow: { from: '', to: '' },
        }
      : {
          kind: 'lounge',
          loungeName: `${flight.departureAirport} Lounge`,
          loungeCode: flight.departureAirport,
          accessWindow: { from: flight.scheduledDeparture, to: flight.scheduledArrival },
        };
  }
  return {
    kind: 'lounge',
    loungeName: payloadString(pass, 'loungeName') ?? PLACEHOLDER,
    loungeCode: payloadString(pass, 'loungeCode') ?? PLACEHOLDER,
    accessWindow: {
      from: payloadString(pass, 'accessFrom') ?? '',
      to: payloadString(pass, 'accessTo') ?? '',
    },
  };
}

/** Flightless travel: every flight-derived value reads '—' (never a fabricated one). */
function withPlaceholders(seed: PassDesign): PassDesign {
  const fields: PassDesign['fields'] = {};
  for (const group of GROUPS) {
    const list = seed.fields[group];
    if (list === undefined) continue;
    fields[group] = list.map((f) => {
      const forced = group === 'auxiliary' && (f.key === 'departure' || f.key === 'status');
      const empty = f.value.trim() === '' || f.value.trim() === '–';
      return forced || empty ? { ...f, value: PLACEHOLDER } : f;
    });
  }
  return { ...seed, fields };
}

/** Refresh a row with this key wherever the design put it, else append it to the back. */
function upsertBack(
  design: PassDesign,
  row: Required<Pick<PassDesignField, 'key' | 'label' | 'value'>>,
): PassDesign {
  let found = false;
  const fields: PassDesign['fields'] = {};
  for (const group of GROUPS) {
    const list = design.fields[group];
    if (list === undefined) continue;
    fields[group] = list.map((f) => {
      if (f.key !== row.key) return f;
      found = true;
      return { ...f, value: row.value };
    });
  }
  if (!found) fields.back = [...(fields.back ?? []), row];
  return { ...design, fields };
}

export function passPreviewDesign(
  pass: DemoPass,
  template: PassDesign,
  /** Partner-level fallbacks the mint uses when the pass carries none. */
  partner?: Pick<DemoPartner, 'assistancePhone' | 'loungeInfoText' | 'esimLinkUrl'> | null,
): PassDesign {
  const travel = pass.templateKind === 'travel';
  const flight = travel ? flightOf(pass) : null;
  let seed = composeSeed(
    pass.templateKind,
    template.style,
    serviceOf(pass, flight),
    flight ?? NO_FLIGHT,
    pass.recipientName,
  );
  if (travel && flight === null) seed = withPlaceholders(seed);

  let design = applyLiveData(template, seed);
  // The seed's semantics carry the real airports — the status wall time
  // ("Leszállt 19:53") reads their clocks.
  if (seed.semantics !== undefined) design = mergePassDesign(design, { semantics: seed.semantics });

  const live = pass.liveStatus;
  const status = live ? STATUS_FOR[live.state] : undefined;
  if (flight !== null && live && status !== undefined) {
    design = applyEventPatchToDesign(design, {
      flight: {
        status,
        delayMinutes: live.delayMinutes ?? 0,
        gate: live.gate,
        terminal: live.terminal,
        actualDeparture: live.actualDeparture,
        actualArrival: live.actualArrival,
      },
    });
  }

  const hotline = payloadString(pass, 'assistancePhone') ?? partner?.assistancePhone?.trim() ?? '';
  if (hotline !== '') {
    design = upsertBack(design, { key: 'hotline', label: '24/7 asszisztencia', value: hotline });
  }
  if (travel) {
    const lounge = payloadString(pass, 'loungeInfoText') ?? partner?.loungeInfoText?.trim() ?? '';
    if (lounge !== '') {
      design = upsertBack(design, { key: 'lounge_info', label: 'Lounge belépés', value: lounge });
    }
  }
  const link = payloadString(pass, 'esimLinkUrl') ?? partner?.esimLinkUrl?.trim() ?? '';
  if (link !== '') {
    design = upsertBack(design, {
      key: 'related_link',
      label: travel ? 'eSIM aktiválás' : 'Kapcsolódó link',
      value: link,
    });
  }
  const policy = pass.templateKind === 'assistance' ? payloadString(pass, 'policyNumber') : null;
  if (policy !== null) {
    design = upsertBack(design, { key: 'policy', label: 'Kötvényszám', value: policy });
  }
  return design;
}
